import React, { useState } from 'react'
import compose from 'recompose/compose'
import { connect as connectRedux, useSelector } from 'react-redux'
import { List, Map } from 'immutable'
import { parse as parseQuery } from '../../services/query-string/index'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import { isEntitled, hasOracleAccessEntitlement } from '../../services/subscription/index'
import { selectOracleAccessProcessing } from '../../services/oracle/selectors'
import { connect as connectStaticText } from '../StaticText/connect'
import Sherpa, { TYPE_STATIC_GRAY_DARK } from '../Sherpa/index'
import { connect as connectPage, PAGE_SEO_TYPE_LOCATION } from '../Page/connect'
import { get as getConfig } from '../../config/index'
import { OracleThemeProvider } from '../OracleCommons/index'
import { useHeaderHeight } from '../OracleCommons/useHeaderHeight'
import { Location, History } from './history-types'
import { StaticTextProvider, InternationalStrings } from '../OracleCommons/StaticTextContext'
import { OracleResponsiveLayout } from './OracleResponsiveLayout'
import { OracleAccessDenied } from './OracleAccessDenied'
import { useOraclePersonalityListQuery, useRateLimitStatusQuery, useGetHistoryQuery } from './queries.generated'
import { shouldApplyRateLimit, getUserTimezone } from './rateLimitUtils'
import { getEnabledPersonalitiesWhereClause } from './personalityFilter'

const oracleTrialEnabled = getConfig().features.ai.oracleTrial

export const queryValue = (key: string) => {
  if (typeof window === 'undefined') return null
  return parseQuery(window.location.search)[key]
}

interface OraclePageProps {
  location: Location;
  history: History;
  staticText: InternationalStrings;
  auth: Map<string, string | number | boolean | undefined | List<number> | number[]>;
}

const OraclePage = ({
  location,
  history,
  staticText,
  auth,
}: OraclePageProps) => {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const headerHeight = useHeaderHeight()
  const oraclePageStyle = { '--oracle-header-height': `${headerHeight}px` } as React.CSSProperties

  // Basic membership checks
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const userIsEntitled = isEntitled(auth.get('subscriptions', List()) as List<number>)

  // Redux-based access (GraphQL)
  const oracleAccessProcessing = useSelector(selectOracleAccessProcessing)

  // Oracle page access: subscription codes [200, 253, 280, 290]
  const hasOracleAccess = hasOracleAccessEntitlement(auth.get('subscriptions', List()) as List<number>)

  // Rate limiting — users with oracle access bypass; everyone else gets trial limits (if flag on)
  const isRateLimitApplicable = shouldApplyRateLimit({ hasOracleAccess })
  const userTimezone = getUserTimezone()

  // Get rate limit status
  const { loading: rateLimitLoading, error: rateLimitError } = useRateLimitStatusQuery({
    skip: !isRateLimitApplicable,
    variables: {
      timezone: userTimezone,
    },
    context: {
      jwt: auth.get('jwt'),
    },
  })

  // Get sidebar favorites chat sessions
  const { loading: sidebarFavoritesLoading, error: sidebarFavoritesError } = useGetHistoryQuery({
    variables: {
      limit: 100,
      offset: 0,
      favorite: true,
      order: [['activityUpdatedAt', 'DESC']],
    },
    context: {
      jwt: auth.get('jwt'),
      useMaster: true,
    },
    fetchPolicy: 'cache-and-network',
  })

  // Get sidebar non-favorites chat sessions
  const {
    loading: sidebarNonFavoritesLoading,
    error: sidebarNonFavoritesError,
  } = useGetHistoryQuery({
    variables: {
      limit: 100,
      offset: 0,
      favorite: false,
      order: [['activityUpdatedAt', 'DESC']],
    },
    context: {
      jwt: auth.get('jwt'),
      useMaster: true,
    },
    fetchPolicy: 'cache-and-network',
  })

  // Get sidebar personality data with where clause
  const {
    loading: sidebarPersonalityLoading,
    error: sidebarPersonalityError,
  } = useOraclePersonalityListQuery({
    variables: {
      limit: 5,
      offset: 0,
      order: [['id', 'ASC']],
      where: getEnabledPersonalitiesWhereClause(),
    },
    context: {
      jwt: auth.get('jwt'),
      useMaster: true,
    },
    fetchPolicy: 'cache-first',
  })

  // Log query errors for debugging (these are background loading operations)
  React.useEffect(() => {
    // eslint-disable-next-line no-console
    if (rateLimitError) console.warn('Rate limit query failed:', rateLimitError)
    // eslint-disable-next-line no-console
    if (sidebarFavoritesError) console.warn('Sidebar favorites query failed:', sidebarFavoritesError)
    // eslint-disable-next-line no-console
    if (sidebarNonFavoritesError) console.warn('Sidebar non-favorites query failed:', sidebarNonFavoritesError)
    // eslint-disable-next-line no-console
    if (sidebarPersonalityError) console.warn('Sidebar personality query failed:', sidebarPersonalityError)
  }, [rateLimitError, sidebarFavoritesError, sidebarNonFavoritesError, sidebarPersonalityError])

  const handleMobileSidebarToggle = () => {
    setMobileSidebarOpen(!mobileSidebarOpen)
  }

  // Block unauthenticated users
  if (!isLoggedIn) {
    return (
      <StaticTextProvider staticText={staticText}>
        <OracleAccessDenied notMember />
      </StaticTextProvider>
    )
  }

  // Create unified loading state - wait for all critical data
  const isInitialLoading = oracleAccessProcessing
    || (isRateLimitApplicable && rateLimitLoading)
    || sidebarFavoritesLoading
    || sidebarNonFavoritesLoading
    || sidebarPersonalityLoading

  if (isInitialLoading) {
    return (
      <div className="oracle-page" style={oraclePageStyle}>
        <div className="oracle-page__loading">
          <Sherpa type={TYPE_STATIC_GRAY_DARK} />
        </div>
      </div>
    )
  }

  // Users with oracle subscription codes → full access
  // Users without → trial (if flag on) or blocked (if flag off)
  if (!hasOracleAccess && !oracleTrialEnabled) {
    return (
      <StaticTextProvider staticText={staticText}>
        <OracleAccessDenied notMember={!userIsEntitled} />
      </StaticTextProvider>
    )
  }

  return (
    <StaticTextProvider staticText={staticText}>
      <OracleThemeProvider containerClass="oracle-sos-container">
        <div className="oracle-page" style={oraclePageStyle}>
          <OracleResponsiveLayout
            history={history}
            location={location}
            mobileSidebarOpen={mobileSidebarOpen}
            onMobileSidebarToggle={handleMobileSidebarToggle}
          />
        </div>
      </OracleThemeProvider>
    </StaticTextProvider>
  )
}

export default compose(
  connectPage({
    seoType: PAGE_SEO_TYPE_LOCATION,
    storeBranch: '',
    storeKey: 'oraclePage',
  }),
  connectStaticText({ storeKey: 'oraclePage' }),
  connectRedux(
    (state: {
      auth: Map<string, string | number | boolean | undefined>,
    }) => ({
      auth: state.auth,
    }),
  ),
)(OraclePage)
